// Lucka 2: Samlad slutdokumentation – innehållsförteckning, status och ett enda nedladdningsläge.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import DocumentHeader from "./DocumentHeader";
import {
  collectHandoverStatus,
  downloadFinalDocumentation,
  type HandoverEntry,
} from "@/lib/project/finalDocumentation";
import DownloadAllDialog from "@/components/Export/DownloadAllDialog";
import { Archive, Download, CheckCircle2, CircleDashed, FileArchive } from "lucide-react";
import HandoverGate from "./HandoverGate";
import { generateHandoverIndexWordDocument } from "@/lib/documentGenerator/basuExecutionGenerators";
import { FileDown } from "lucide-react";


interface Props {
  projectId: string;
}

const FinalDocumentation: React.FC<Props> = ({ projectId }) => {
  const [entries, setEntries] = useState<HandoverEntry[]>([]);
  const [percent, setPercent] = useState(0);
  const [done, setDone] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showAll, setShowAll] = useState(false);


  const load = useCallback(async () => {
    setLoading(true);
    try {
      const status = await collectHandoverStatus(projectId);
      setEntries(status.entries);
      setPercent(status.percent);
      setDone(status.done);
    } catch {
      toast.error("Kunde inte läsa slutdokumentationen");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDownload = async () => {
    setExporting(true);
    try {
      await downloadFinalDocumentation(projectId);
      toast.success("Slutdokumentationen är nedladdad");
    } catch {
      toast.error("Kunde inte skapa paketet");
    } finally {
      setExporting(false);
    }
  };

  const exportWord = async () => {
    try {
      await generateHandoverIndexWordDocument(projectId, entries, percent);
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  const missing = entries.filter((e) => !e.ok);

  return (
    <div className="space-y-6">
      <DocumentHeader
        docNumber="34"
        title="Slutdokumentation till förvaltning"
        description="Hela arbetsmiljödokumentationen som ett paket med innehållsförteckning, underlag i CSV och maskinläsbar projektfil."
        icon={Archive}
        projectId={projectId}
        exportHistory={{ documentType: "handover_index" }}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={exportWord} disabled={loading}>
              <FileDown className="mr-2 h-4 w-4" />
              Exportera Dok 34
            </Button>
            <Button onClick={() => setShowAll(true)} disabled={exporting || loading}>
              <FileArchive className="mr-2 h-4 w-4" />
              Ladda ner alla dokument (Word)
            </Button>
            <Button variant="outline" onClick={handleDownload} disabled={exporting || loading}>
              <Download className="mr-2 h-4 w-4" />
              {exporting ? "Skapar paketet…" : "Ladda ner allt"}
            </Button>
          </div>
        }
      />

      <DownloadAllDialog projectId={projectId} open={showAll} onOpenChange={setShowAll} />


      <Card>
        <CardHeader>
          <CardTitle className="text-base">Färdigt att lämna över</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Progress value={percent} />
          <p className="text-sm text-muted-foreground">
            {done} av {entries.length} handlingar finns i projektet ({percent} %).
            {missing.length > 0 && " Paketet kan laddas ner ändå, men det som saknas listas som lucka."}
          </p>
        </CardContent>
      </Card>

      <HandoverGate projectId={projectId} entries={entries} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Innehållsförteckning</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading ? (
            <p className="text-sm text-muted-foreground">Laddar…</p>
          ) : (
            entries.map((e) => (
              <div
                key={e.docNumber + e.title}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
              >
                <div className="flex items-center gap-2">
                  {e.ok ? (
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                  ) : (
                    <CircleDashed className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="text-sm font-medium">
                    Dok {e.docNumber} · {e.title}
                  </span>
                  <Badge variant="outline">{e.owner}</Badge>
                </div>
                <span className={`text-sm ${e.ok ? "text-muted-foreground" : "text-destructive"}`}>
                  {e.detail}
                </span>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FinalDocumentation;
